/**
 * Real-or-fake swipe rounds — the rules behind Course of Course and Fact or Fiction.
 *
 * A deck is a list of cards, each marked real or made up, with a difficulty tier. A round
 * draws a fixed number of cards, half real, ramping from easy fakes to hard ones, and shows
 * them one at a time. The player answers real or fake within a window. Wrong or late costs
 * a time penalty. The score is the clock when the last card is answered. Lower is better.
 *
 * Pure: `now` (ms) is always passed in, so it is testable and pauses cleanly.
 */

export type Tier = 'easy' | 'medium' | 'hard';

export interface Card {
  id: string;
  /** the headline on the card */
  text: string;
  /** a smaller line under it (e.g. the department) */
  sub?: string;
  real: boolean;
  tier?: Tier;
  /** what to say after the answer ("Real — ENVS 220", "Made up: it's actually …") */
  reveal?: string;
}

export interface Deck {
  version: number;
  title: string;
  source?: string;
  cards: Card[];
}

export interface SwipeRules {
  /** cards per round */
  cards: number;
  windowMs: number;
  penaltyMs: number;
}

export const DEFAULT_SWIPE_RULES: SwipeRules = { cards: 24, windowMs: 6000, penaltyMs: 3000 };

const TIER_ORDER: Record<Tier, number> = { easy: 0, medium: 1, hard: 2 };

function shuffle<T>(list: T[], rng: () => number): T[] {
  const a = list.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * Draw a round: half real, half fake. Fakes are sorted easy → hard so the round ramps;
 * reals are sprinkled in at random positions so the pattern never gives the answer away.
 */
export function drawRound(deck: Card[], count: number, rng: () => number = Math.random): Card[] {
  const reals = shuffle(deck.filter((c) => c.real), rng);
  const fakes = shuffle(deck.filter((c) => !c.real), rng);
  const nFake = Math.min(fakes.length, Math.floor(count / 2));
  const nReal = Math.min(reals.length, count - nFake);
  const pickedFakes = fakes.slice(0, nFake).sort((a, b) => TIER_ORDER[a.tier ?? 'medium'] - TIER_ORDER[b.tier ?? 'medium']);
  const pickedReals = reals.slice(0, nReal);
  // merge: walk the ramped fakes in order, insert reals at random slots
  const out: Card[] = pickedFakes.slice();
  for (const r of pickedReals) {
    const at = Math.floor(rng() * (out.length + 1));
    out.splice(at, 0, r);
  }
  return out;
}

export interface SwipeOutcome {
  ok: boolean;
  card: Card;
  /** what the player said; 'timeout' when the window closed */
  did: boolean | 'timeout';
  won: boolean;
}

export type Status = 'idle' | 'playing' | 'won';

export class SwipeRound {
  readonly rules: SwipeRules;
  private cards: Card[] = [];
  private index = 0;
  private cardAt = 0;
  private startAt = 0;
  private penalties = 0;
  private pausedTotal = 0;
  private pausedAt: number | null = null;
  private status: Status = 'idle';
  private rng: () => number;
  private deck: Card[];
  mistakes = 0;
  streak = 0;
  bestStreak = 0;

  constructor(deck: Card[], rules: Partial<SwipeRules> = {}, rng: () => number = Math.random) {
    this.deck = deck;
    this.rules = { ...DEFAULT_SWIPE_RULES, ...rules };
    this.rng = rng;
  }

  start(now: number): void {
    this.cards = drawRound(this.deck, this.rules.cards, this.rng);
    this.index = 0;
    this.mistakes = 0;
    this.streak = 0;
    this.bestStreak = 0;
    this.penalties = 0;
    this.pausedTotal = 0;
    this.pausedAt = null;
    this.startAt = now;
    this.cardAt = now;
    this.status = this.cards.length ? 'playing' : 'won';
  }

  get current(): Card | null {
    return this.status === 'playing' ? this.cards[this.index] : null;
  }

  /** the card after the current one, for the view to show peeking underneath */
  get next(): Card | null {
    return this.status === 'playing' ? (this.cards[this.index + 1] ?? null) : null;
  }

  get total(): number {
    return this.cards.length;
  }

  get done(): number {
    return this.index;
  }

  get state(): Status {
    return this.status;
  }

  get paused(): boolean {
    return this.pausedAt !== null;
  }

  elapsed(now: number): number {
    if (this.status === 'idle') return 0;
    const end = this.pausedAt ?? now;
    return Math.max(0, end - this.startAt - this.pausedTotal + this.penalties);
  }

  remaining(now: number): number {
    if (this.status !== 'playing' || this.pausedAt !== null) return 1;
    return Math.max(0, 1 - (now - this.cardAt) / this.rules.windowMs);
  }

  pause(now: number): void {
    if (this.status === 'playing' && this.pausedAt === null) this.pausedAt = now;
  }

  resume(now: number): void {
    if (this.pausedAt !== null) {
      const gap = now - this.pausedAt;
      this.pausedTotal += gap;
      this.cardAt += gap;
      this.pausedAt = null;
    }
  }

  tick(now: number): SwipeOutcome | null {
    if (this.status !== 'playing' || this.pausedAt !== null) return null;
    if (now - this.cardAt < this.rules.windowMs) return null;
    return this.resolve('timeout', now);
  }

  answer(real: boolean, now: number): SwipeOutcome | null {
    if (this.status !== 'playing' || this.pausedAt !== null) return null;
    return this.resolve(real, now);
  }

  private resolve(did: boolean | 'timeout', now: number): SwipeOutcome {
    const card = this.cards[this.index];
    const ok = did !== 'timeout' && did === card.real;
    if (ok) {
      this.streak++;
      this.bestStreak = Math.max(this.bestStreak, this.streak);
    } else {
      this.mistakes++;
      this.streak = 0;
      this.penalties += this.rules.penaltyMs;
    }
    this.index++;
    this.cardAt = now;
    const won = this.index >= this.cards.length;
    if (won) this.status = 'won';
    return { ok, card, did, won };
  }
}
